<script setup lang="ts">
import {
    Bell,
    Bot,
    CalendarDays,
    ChartNoAxesColumn,
    FileText,
    LayoutDashboard,
    Search,
    Settings as SettingsIcon,
    Sparkles,
} from '@lucide/vue';

const nav = [
    { label: 'Overview', icon: LayoutDashboard, active: true },
    { label: 'Content', icon: FileText, active: false },
    { label: 'Calendar', icon: CalendarDays, active: false },
    { label: 'AI Assistant', icon: Bot, active: false },
    { label: 'Analytics', icon: ChartNoAxesColumn, active: false },
    { label: 'Settings', icon: SettingsIcon, active: false },
];

const stats = [
    { label: 'Published posts', value: '24' },
    { label: 'Scheduled posts', value: '12' },
    { label: 'Pending approval', value: '4' },
];

const content = [
    {
        title: 'Product launch teaser',
        meta: 'Instagram post · Thu, 15 Oct',
        status: 'Scheduled',
    },
    {
        title: 'Behind the scenes',
        meta: 'Facebook post · Fri, 16 Oct',
        status: 'In review',
    },
    {
        title: 'How to get started',
        meta: 'YouTube video · Mon, 19 Oct',
        status: 'Draft',
    },
];

const days = Array.from({ length: 28 }, (_, index) => index + 1);
const highlightedDays = [7, 14, 21];
</script>

<template>
    <div
        aria-hidden="true"
        class="border-border/70 bg-card overflow-hidden rounded-2xl border shadow-lg select-none"
    >
        <div
            class="border-border/70 bg-muted/50 flex items-center gap-2 border-b px-4 py-3"
        >
            <span class="bg-muted-foreground/30 size-2.5 rounded-full" />
            <span class="bg-muted-foreground/30 size-2.5 rounded-full" />
            <span class="bg-muted-foreground/30 size-2.5 rounded-full" />
            <span class="text-muted-foreground ml-2 text-xs">
                LaunchStation workspace
            </span>
        </div>

        <div class="flex">
            <aside
                class="border-border/70 hidden w-44 shrink-0 border-r p-3 sm:block"
            >
                <div class="mb-4 flex items-center gap-2 px-1">
                    <img src="/icon.svg" alt="" class="size-5" />
                    <span class="text-sm font-medium">LaunchStation</span>
                </div>
                <nav class="grid gap-0.5">
                    <span
                        v-for="item in nav"
                        :key="item.label"
                        :class="[
                            'flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs',
                            item.active
                                ? 'bg-primary/10 text-foreground font-medium'
                                : 'text-muted-foreground',
                        ]"
                    >
                        <component :is="item.icon" class="size-3.5" />
                        {{ item.label }}
                    </span>
                </nav>
            </aside>

            <div class="min-w-0 flex-1 p-4 sm:p-5">
                <div class="mb-4 flex items-center gap-3">
                    <div
                        class="border-border bg-muted/40 text-muted-foreground flex flex-1 items-center gap-2 rounded-lg border px-3 py-1.5 text-xs"
                    >
                        <Search class="size-3.5" />
                        Search content, campaigns, or ask AI
                    </div>
                    <Bell class="text-muted-foreground size-4" />
                    <span
                        class="bg-secondary/25 size-6 shrink-0 rounded-full"
                    />
                </div>

                <p class="font-display text-lg leading-tight">Good morning!</p>
                <p class="text-muted-foreground text-xs">
                    Here is what is happening with your marketing today.
                </p>

                <div class="mt-4 grid grid-cols-3 gap-2">
                    <div
                        v-for="stat in stats"
                        :key="stat.label"
                        class="border-border rounded-lg border p-3"
                    >
                        <p class="text-muted-foreground text-[10px]">
                            {{ stat.label }}
                        </p>
                        <p class="tabular-numbers text-lg font-semibold">
                            {{ stat.value }}
                        </p>
                    </div>
                </div>

                <div class="mt-3 grid gap-3 lg:grid-cols-2">
                    <div class="border-border rounded-lg border p-3">
                        <p class="mb-2 text-xs font-medium">Recent content</p>
                        <div class="grid gap-2">
                            <div
                                v-for="item in content"
                                :key="item.title"
                                class="flex items-center gap-2"
                            >
                                <span
                                    class="bg-muted size-7 shrink-0 rounded-md"
                                />
                                <span class="min-w-0 flex-1">
                                    <span
                                        class="block truncate text-[11px] font-medium"
                                    >
                                        {{ item.title }}
                                    </span>
                                    <span
                                        class="text-muted-foreground block truncate text-[10px]"
                                    >
                                        {{ item.meta }}
                                    </span>
                                </span>
                                <span
                                    class="bg-muted text-muted-foreground shrink-0 rounded-full px-1.5 py-0.5 text-[10px]"
                                >
                                    {{ item.status }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="border-border rounded-lg border p-3">
                        <p class="mb-2 text-xs font-medium">Content calendar</p>
                        <div class="grid grid-cols-7 gap-1">
                            <span
                                v-for="day in days"
                                :key="day"
                                :class="[
                                    'flex aspect-square items-center justify-center rounded text-[10px]',
                                    highlightedDays.includes(day)
                                        ? 'bg-primary/15 text-foreground font-medium'
                                        : 'text-muted-foreground',
                                ]"
                            >
                                {{ day }}
                            </span>
                        </div>
                    </div>
                </div>

                <div
                    class="border-primary/25 bg-primary/5 mt-3 flex items-center gap-2 rounded-lg border p-3"
                >
                    <Sparkles class="text-primary size-4 shrink-0" />
                    <p class="text-xs">
                        <span class="font-medium">
                            Your assistant prepared 3 posts.
                        </span>
                        Review them before they go out.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
